import React from "react";
import { styled } from "styled-components";

import {
  colors,
  desktop,
  Modal,
  Pill,
  tablet,
  Text,
  VisuallyHidden,
} from "@vericus/cadmus-ui";

import { create } from "@ebay/nice-modal-react";
import { format } from "date-fns";

import { DuplicatePastAssessmentCallback } from "@/data/templates";
import {
  AssessmentFragment,
  AssessmentPreset,
  AssessmentSettingsFragment,
  AssessmentType,
  ExamTiming,
  InstructionSheetFragment,
  RequirementsFragment,
  TaskKind,
  usePastInstructionSheetQuery,
} from "@/generated/graphql";
import { sheetPreset } from "@/graphql/task-selectors";
import { selectAssessmentNoun } from "@/ui/task/assessment-utils";

import { AssessmentPreview } from "./assessment-preview";
import { Header } from "./components";
import {
  Container,
  HeaderContentsContainer,
  InfoContainer,
  PaddedPreviewContainer,
  StickyHeaderContainer,
  useOnBack,
  useOnCloseModal,
} from "./modals-shared";
import { PreviewModalSkeleton } from "./preview-modal-skeleton";
import { useOnDuplicatePastAssessment } from "./use-on-duplicate-past-assessment";

export interface Requirements
  extends Omit<
    RequirementsFragment,
    | "noDraft"
    | "noLimit"
    | "noReferencing"
    | "noTimeLimit"
    | "noWeight"
    | "noExamReadingTime"
    | "__typename"
  > {}

export interface PastAssessmentPreviewModalProps {
  /** Populated header contents with information about the past assessment. */
  headerContents: JSX.Element;
  /** The past assessment being previewed. */
  pastAssessment: Pick<AssessmentFragment, "id" | "name" | "subject">;
  pastInstructionSheet: InstructionSheetFragment;
  assessmentSettings: AssessmentSettingsFragment;
  taskKind: TaskKind;
  /**
   * Called when the user indicates they want to use (duplicate) the past assessment being previewed.  */
  onConfirm: DuplicatePastAssessmentCallback;
  /** Called when the user wants to go back to where the modal was open from. */
  onGoBack: VoidFunction;
  /** Called when the user wants to close the modal. */
  onClose: VoidFunction;
}

export interface ConnectedPastAssessmentPreviewModalProps {
  /** Past assessment details being previewed. */
  pastAssessment: Pick<
    AssessmentFragment,
    "id" | "name" | "subject" | "settings"
  >;
  /** The ID (version) of the instruction sheet to preview. */
  instructionSheetVersionId: string;
}

export const ConnectedPastAssessmentPreviewModal =
  create<ConnectedPastAssessmentPreviewModalProps>(
    ({ pastAssessment, instructionSheetVersionId }) => {
      const closeModal = useOnCloseModal();
      const onGoBack = useOnBack();

      const { loading, data } = usePastInstructionSheetQuery({
        variables: {
          assessmentId: pastAssessment.id,
          sheetId: instructionSheetVersionId,
        },
      });
      const onConfirm = useOnDuplicatePastAssessment();

      if (loading || !data?.instructionSheet || !pastAssessment.settings) {
        return (
          <Modal.Root
            onOpenChange={(open) => {
              if (!open) closeModal();
            }}
          >
            <StyledModalContent
              aria-describedby={undefined}
              aria-labelledby={undefined}
            >
              <PreviewModalSkeleton />
            </StyledModalContent>
          </Modal.Root>
        );
      }

      let dateLabel = <></>;

      const assessmentNoun = selectAssessmentNoun(pastAssessment.settings);

      switch (pastAssessment.settings?.assessmentType) {
        case AssessmentType.Exam:
          dateLabel =
            pastAssessment.settings.examTiming === ExamTiming.Live ? (
              <>
                <Text kind="systemMd">
                  {assessmentNoun} start:{" "}
                  {pastAssessment.settings.examStartDate != null &&
                    formatDueDate(pastAssessment.settings.examStartDate)}
                </Text>
              </>
            ) : (
              <>
                <Text kind="systemMd">
                  {assessmentNoun} open:{" "}
                  {pastAssessment.settings.examStartDate != null &&
                    formatDueDate(pastAssessment.settings.examStartDate)}
                </Text>
              </>
            );
          break;

        case AssessmentType.Assignment:
        default:
          dateLabel = (
            <>
              <Text kind="systemMd">
                Due date:{" "}
                {pastAssessment.settings?.dueDate != null &&
                  formatDueDate(pastAssessment.settings?.dueDate)}
              </Text>
            </>
          );
      }

      /**
       * Remove dates from the preview, as they're not relevant in the event
       * that the user wants to duplicate the assessment.
       */
      const assessmentSettings = {
        ...pastAssessment.settings,
        dueDate: null,
        draftDueDate: null,
        returnDate: null,
        examStartDate: null,
        examEndDate: null,
        examFeedbackDate: null,
      };

      return (
        <PastAssessmentPreviewModal
          headerContents={
            <PastAssessmentHeaderContents
              name={pastAssessment.name}
              subjectName={pastAssessment.subject.name}
              dateLabel={dateLabel}
              preset={sheetPreset(
                data.instructionSheet.kind,
                data.instructionSheet.format
              )}
            />
          }
          pastAssessment={pastAssessment}
          pastInstructionSheet={data.instructionSheet}
          assessmentSettings={assessmentSettings}
          taskKind={data.instructionSheet.kind}
          onConfirm={onConfirm}
          onGoBack={onGoBack}
          onClose={closeModal}
        />
      );
    }
  );

const formatDueDate = (dueDate: string): string => {
  return format(new Date(dueDate), "d MMM y");
};

export function PastAssessmentPreviewModal(
  props: PastAssessmentPreviewModalProps
) {
  const {
    pastAssessment,
    headerContents,
    pastInstructionSheet: {
      serverId: instructionSheetVersionId,
      resources,
      format: taskFormat,
      rawContent: editorContent,
      questionBlocks,
    },
    assessmentSettings,
    onClose,
    onConfirm,
    onGoBack,
  } = props;
  const { id: pastAssessmentId, name } = pastAssessment;

  return (
    <Modal.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <StyledModalContent withAnimation={false} withOverlay={false}>
        <Container backgroundColor="#F5F7FA">
          <StickyHeaderContainer>
            <Modal.Title asChild>
              <Header
                backgroundColor="#F5F7FA"
                confirmButtonText="Reuse assessment"
                onConfirm={() =>
                  onConfirm(pastAssessmentId, name, instructionSheetVersionId)
                }
                onGoBack={onGoBack}
              >
                {headerContents}
              </Header>
            </Modal.Title>
          </StickyHeaderContainer>

          <VisuallyHidden.Root>
            <Modal.Description>
              Showing a preview of the released past assessment
            </Modal.Description>
          </VisuallyHidden.Root>

          <PaddedPreviewContainer>
            <div style={{ backgroundColor: "white", borderRadius: "8px" }}>
              <AssessmentPreview
                assessmentName={name}
                assessmentSettings={assessmentSettings}
                taskKind={props.taskKind}
                content={editorContent}
                questionBlocks={questionBlocks}
                resources={resources}
                taskFormat={taskFormat}
                showHidden
              />
            </div>
          </PaddedPreviewContainer>
        </Container>
      </StyledModalContent>
    </Modal.Root>
  );
}

const StyledModalContent = styled(Modal.Content)`
  display: flex;
  flex-direction: column;
  height: 90vh;
  width: 575px;
  ${tablet`
    width: 936px;
  `}
  ${desktop`
    width: 1090px;
  `}
`;

interface PastAssessmentHeaderContentProps {
  name: string;
  subjectName: string;
  dateLabel: React.ReactNode;
  /**
   * The preset this sheet was released from. A group destination keeps its own
   * preset, so this is what the teacher is picking, not always what they get.
   */
  preset: AssessmentPreset;
}

export const PastAssessmentHeaderContents = ({
  name,
  subjectName,
  dateLabel,
  preset,
}: PastAssessmentHeaderContentProps) => {
  return (
    <HeaderContentsContainer>
      <Modal.Title asChild>
        <Text kind="headingFour" style={{ paddingTop: 0 }}>
          {name}
        </Text>
      </Modal.Title>
      <InfoContainer>
        <Text kind="systemMd">{subjectName}</Text>
        <Text kind="systemMd">·</Text>
        {dateLabel}
        {preset === AssessmentPreset.Viva && (
          <Pill color={colors.purple200} as="div" basic>
            <Text kind="label">Viva</Text>
          </Pill>
        )}
      </InfoContainer>
    </HeaderContentsContainer>
  );
};
